import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { type ComponentPropsWithRef, type ElementRef, forwardRef, memo, type ReactNode } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const treeItem = tailwindVariants({
  base: 'group/treeItem relative z-1 flex shrink-0 items-center gap-2 rounded-2 px-2 ads:text-secondary text-gray-11 outline outline-2 outline-transparent -outline-offset-2 hover:ads:bg-subtle hover:bg-gray-3 [.dragging_&]:opacity-50 [.hovering-drop_&]:bg-gray-3 ads:[.hovering-drop_&]:bg-subtle [.hovering-drop_&]:outline-macaw-300 ads:[.hovering-drop_&]:outline-focus [.moving-item_&]:opacity-50',
  defaultVariants: {
    size: 'medium',
    state: 'default',
  },
  variants: {
    size: {
      medium: 'h-9 text-16',
      small: 'h-8 text-14',
    },
    state: {
      active: 'bg-gray-4',
      default: 'bg-transparent',
      selected: 'ads:bg-primary-subtle bg-blue-1 hover:ads:bg-primary-subtle hover:bg-blue-2',
    },
  },
});

export const TREE_ITEM_ELEMENT_TYPE = 'div';

export type TreeItemVariants = VariantProps<typeof treeItem>;

export type TreeItemProps = Omit<ComponentPropsWithRef<typeof TREE_ITEM_ELEMENT_TYPE>, 'open' | 'prefix'> &
  Pick<IconButtonProps, 'disabled'> &
  TreeItemVariants & {
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
  };

const _TreeItem = forwardRef<ElementRef<typeof TREE_ITEM_ELEMENT_TYPE>, TreeItemProps>(
  (
    {
      children,
      className,
      defaultOpen,
      disabled,
      onOpenChange,
      open: openProp,
      prefix,
      size,
      state,
      suffix,
      ...restOfProps
    },
    forwardedRef,
  ) => {
    const [open = false, setOpen] = useControllableState({
      defaultProp: defaultOpen,
      onChange: onOpenChange,
      prop: openProp,
    });

    const label = open ? 'Collapse' : 'Expand';

    return (
      <div
        className={treeItem({ class: className, size, state })}
        data-testid="TREE_ITEM"
        ref={forwardedRef}
        {...restOfProps}
      >
        <Tooltip label={label}>
          <IconButton
            appearance="ghost"
            className="z-1 shrink-0"
            color="gray"
            data-testid={open ? 'TREE_ITEM_COLLAPSE_BUTTON' : 'TREE_ITEM_EXPAND_BUTTON'}
            disabled={disabled}
            label={label}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => setOpen((prevOpen) => !prevOpen)}
            size="extra-small"
          >
            <ChevronRightIcon
              className={classNames('transition-transform', open ? 'rotate-90' : 'rotate-0')}
              size="sm"
            />
          </IconButton>
        </Tooltip>

        {prefix && <div className="shrink-0">{prefix}</div>}

        {children}

        {suffix && <div className="shrink-0">{suffix}</div>}
      </div>
    );
  },
);

_TreeItem.displayName = '_TreeItem';

export const TreeItem = memo(_TreeItem);
